/**
 * What a custom indicator IS, as data.
 *
 * A script is source code plus a declaration of the knobs it takes and the
 * series it draws. The declaration is separate from the code on purpose: the
 * application has to render an input form, allocate colours, decide whether the
 * study sits on the price pane or its own, and store the trader's chosen
 * settings — all before a single line of the script has run, and while the
 * script may be one that never runs successfully at all.
 *
 * ## Nothing here is a colour, a pixel or a font
 *
 * `colorIndex` is an index into the theme palette, exactly as it is for
 * drawings. A shared script cannot pick `#ff0000`; it picks "the third
 * accent", and a theme change carries it along. That also removes an entire
 * class of hostile input — a script cannot make its plot invisible against the
 * background, or the same colour as the price line.
 */

/** The series a script can ask for by name, rather than reading `bars` itself. */
export const SCRIPT_SOURCES = [
  'open', 'high', 'low', 'close', 'hl2', 'hlc3', 'ohlc4', 'volume',
] as const;
export type ScriptSourceName = (typeof SCRIPT_SOURCES)[number];

export const SCRIPT_INPUT_KINDS = ['number', 'integer', 'boolean', 'source', 'select'] as const;
export type ScriptInputKind = (typeof SCRIPT_INPUT_KINDS)[number];

/**
 * One knob on the settings form.
 *
 * `min`/`max` are not advisory. They are enforced when the trader's saved
 * value is resolved, so a length of 2,000,000 cannot reach a loop inside the
 * Worker — bounding the input is cheaper than bounding the loop.
 */
export interface ScriptInputDecl {
  readonly id: string;
  readonly label: string;
  readonly kind: ScriptInputKind;
  readonly def: number | boolean | string;
  readonly min?: number;
  readonly max?: number;
  /** For `select` only: the allowed values, in the order they are offered. */
  readonly options?: readonly string[];
}

export const SCRIPT_PLOT_STYLES = ['line', 'stepline', 'histogram', 'area', 'dots'] as const;
export type ScriptPlotStyle = (typeof SCRIPT_PLOT_STYLES)[number];

/** One series the script draws. */
export interface ScriptPlotDecl {
  readonly id: string;
  readonly label: string;
  readonly style: ScriptPlotStyle;
  /** Index into the theme palette — never a colour. See the file header. */
  readonly colorIndex: number;
  readonly lineWidth: number;
}

/**
 * A script, as stored and as shared.
 *
 * `fingerprint` is derived from `source` and is recomputed by the server on
 * every write rather than accepted from a client. A client that could choose
 * its own fingerprint could make new code carry the hash of code a trader
 * already reviewed, which is the one thing the fingerprint exists to prevent.
 */
export interface ScriptRecordLike {
  readonly id: string;
  readonly name: string;
  readonly source: string;
  readonly fingerprint: string;
  readonly overlay: boolean;
  readonly inputs: readonly ScriptInputDecl[];
  readonly plots: readonly ScriptPlotDecl[];
  readonly status: ScriptStatus;
}

/**
 * Where a script stands with respect to other people.
 *
 * - `private` — only the author has it.
 * - `shared`  — anyone with the share code can install it.
 * - `removed` — taken down. Installers keep the row so the app can say what
 *   happened, and the runner refuses to execute it. A takedown that only
 *   stopped NEW installs would leave the existing ones running forever.
 */
export const SCRIPT_STATUSES = ['private', 'shared', 'removed'] as const;
export type ScriptStatus = (typeof SCRIPT_STATUSES)[number];

/* --- limits ---
   Every one of these bounds something a stranger's script would otherwise
   choose for us. They live together so the set can be read in one go, and so a
   new limit is obviously a new line rather than a magic number at a call site. */

/** Longest script name. Long enough to be descriptive, short enough for a legend. */
export const MAX_SCRIPT_NAME = 48;
/** 64KB of source. Larger than any indicator anyone writes by hand. */
export const MAX_SCRIPT_SOURCE_BYTES = 64 * 1024;
export const MAX_SCRIPT_INPUTS = 12;
export const MAX_SCRIPT_PLOTS = 6;
/** Longest identifier for an input or a plot. */
export const MAX_SCRIPT_ID = 32;
/** Options offered by one `select` input. */
export const MAX_SCRIPT_OPTIONS = 12;

/**
 * Most bars handed to a script in one run.
 *
 * The visible window on a chart, generously. It bounds the payload that is
 * cloned across the Worker boundary and the arrays the script allocates, and
 * it is the only bound on memory that exists — see `sandbox.ts`.
 */
export const MAX_SCRIPT_BARS = 5_000;

/**
 * How long one run may take before the host terminates the Worker.
 *
 * Enforced by the HOST with `terminate()`, never inside the Worker: a
 * synchronous `while (true) {}` cannot be interrupted from inside the thread it
 * is running on, so any in-Worker timeout would be checked by code that never
 * gets to run. Generous — a 5,000-bar moving average is well under a
 * millisecond — because a false timeout on a slow phone is a working script
 * that appears broken.
 */
export const SCRIPT_TIMEOUT_MS = 750;

/**
 * How many timeouts in a row before the script is switched off.
 *
 * Without this, a script that hangs every run burns a CPU core for as long as
 * the chart is open, silently, on the trader's machine during their session.
 */
export const MAX_SCRIPT_TIMEOUTS = 3;
